import type { Segment, SegmentType } from '@shared/types.ts';
import type { IconName } from '../../components/Icon.tsx';
import { formatClock, formatDuration, toInstant } from '@shared/time.ts';
import { flightAirline } from '@shared/airlines.ts';

export const TYPE_LABEL: Record<SegmentType, string> = {
  flight: 'Flight',
  stay: 'Stay',
  car: 'Car rental',
  train: 'Train',
  reservation: 'Reservation',
  activity: 'Activity',
};

export function segmentIcon(type: SegmentType): IconName {
  return ({ flight: 'plane', stay: 'home', car: 'car', train: 'train', reservation: 'fork', activity: 'ticket' } as const)[type];
}

export function sortSegments(segments: Segment[]): Segment[] {
  return [...segments].sort((a, b) => toInstant(a.start).getTime() - toInstant(b.start).getTime());
}

/** Timeline entries: a stay appears on check-in and again on check-out. */
export interface TimelineEntry {
  key: string;
  segment: Segment;
  date: string;
  time?: string;
  kind: 'start' | 'checkout';
  sortKey: number;
}

export function timelineEntries(segments: Segment[]): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  for (const s of segments) {
    out.push({ key: `${s.id}-s`, segment: s, date: s.start.date, time: s.start.time, kind: 'start', sortKey: toInstant(s.start).getTime() });
    if (s.type === 'stay' && s.end?.date && s.end.date !== s.start.date) {
      out.push({ key: `${s.id}-e`, segment: s, date: s.end.date, time: s.end.time, kind: 'checkout', sortKey: toInstant(s.end).getTime() - 1 });
    }
  }
  return out.sort((a, b) => a.sortKey - b.sortKey);
}

/** One-line title + subtitle for a timeline row. */
export function entryText(e: TimelineEntry): { title: string; sub: string; time: string } {
  const s = e.segment;
  const time = e.time ? formatClock(e.time) : '';
  if (e.kind === 'checkout') return { title: 'Check out', sub: s.place?.name ?? s.title, time };
  switch (s.type) {
    case 'flight': {
      const arrive = s.end?.time ? `lands ${formatClock(s.end.time)}${s.end.date !== s.start.date ? ` ${shortDay(s.end.date)}` : ''}` : '';
      const flightName = [flightAirline(s), s.number].filter(Boolean).join(' ');
      return { title: `${s.from?.code ?? '?'} → ${s.to?.code ?? '?'}`, sub: [flightName, s.confirmation, arrive].filter(Boolean).join(' · '), time };
    }
    case 'train':
      return { title: s.title, sub: [s.number, s.end?.time ? `arrives ${formatClock(s.end.time)}` : ''].filter(Boolean).join(' · '), time };
    case 'stay': {
      const nights = s.end?.date ? nightsBetween(s.start.date, s.end.date) : 0;
      return { title: `Check in · ${s.place?.name ?? s.title}`, sub: [nights ? `${nights} night${nights === 1 ? '' : 's'}` : '', s.place?.city].filter(Boolean).join(' · '), time };
    }
    case 'car':
      return { title: s.title, sub: s.place?.name ?? '', time };
    case 'reservation':
      return { title: s.place?.name ?? s.title, sub: s.details['Party size'] ? `Table for ${s.details['Party size']}` : s.place?.city ?? '', time };
    case 'activity':
      return { title: s.title, sub: s.details.Tickets ? `Tickets for ${s.details.Tickets}` : s.place?.name ?? '', time };
  }
}

function shortDay(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${d}`;
}

function nightsBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
}

/** Apple Maps for the address, else the place name and city. */
export function mapsUrl(place: Segment['place']): string | undefined {
  const q = place?.address ?? [place?.name, place?.city].filter(Boolean).join(', ');
  return q ? `https://maps.apple.com/?q=${encodeURIComponent(q)}` : undefined;
}

export function flightDuration(s: Segment): string {
  return s.end ? formatDuration(s.start, s.end) : '';
}

/** Initials of household members among the booking's travelers. */
export function travelerInitials(travelers: string[], people: Array<{ name: string; initial: string; bookingNames: string[] }>): string[] {
  const out: string[] = [];
  for (const p of people) {
    const names = [p.name, ...p.bookingNames].map((n) => n.toLowerCase());
    if (travelers.some((t) => names.some((n) => t.toLowerCase().includes(n) || n.includes(t.toLowerCase().replace(/[\[\]]/g, ''))))) out.push(p.initial);
  }
  return out;
}
